// Ending a run, on purpose.
//
// ⚠ WHY THIS EXISTS. Until 2026-09-23 nothing could close a run. The route was
// there; no screen, tool or runbook step called it. Every run stayed OPEN for
// ever, and on day two the line picker found yesterday's run "already open for
// that product" and told the operator to go back and use it — so today's checks
// would have landed on a run that owed nothing, released by yesterday's
// start-up.
//
// Two taps, and the second one says what closing will record, because closing
// is the moment the run is judged: every check that was due and not taken is
// booked missed, and so is every start-up check answered but not verified.
// Checks scheduled for later are removed instead — the run ended before they
// were wanted (runs.ts, tools/runWrites.ts).
//
// ⚠ IT WILL NOT CLOSE WHILE THIS DEVICE STILL HOLDS READINGS. A reading that
// reaches the server after the run is closed is refused `run_closed`, so closing
// with the queue unsent would turn checks the operator took into refusals. An
// unreadable queue blocks too: unknown is not zero (see OperatorShell's
// handover, which refuses on the same grounds).
//
// ⚠ THE COUNTS ARE READ WHEN THE PANEL OPENS, BY THE PANEL (UI/UX audit, 31 Oct
// 2026, UX-40). The panel used to be handed one number from the screen behind it
// - every OWED row due - and said "32 checks ... will be recorded as MISSED" over
// a walk-around reading "1 of 31 answered · 1 waiting on a signature". Both were
// right about different things: the 32 was 30 unanswered start-up checks plus 2
// product checks, and the answered-but-unverified one was a separate sentence. And
// the screen's list was read when the run loaded, not when somebody went to close
// it. Now the panel asks the server for the OWED and the AWAITING_VERIFY rows as
// it opens and says the three things the server will book missed, by name - the
// same three the close route's UPDATE selects. What closing RECORDS is unchanged
// (rule 17); only how it is counted and said.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api';
import { opFocus, opMessage } from '../../shells/OperatorShell';

/** The obligation fields the close counts need, as GET /runs/{id}/obligations sends them. */
export interface CloseObligation {
  kind: 'FIRST_OFF' | 'ADHERENCE' | 'READINESS';
  dueAt: string;
  status: string;
}

/** What closing would record, by kind. */
export interface CloseCounts {
  /** Start-up checks with no accepted answer (READINESS, OWED): booked missed. */
  startupUnanswered: number;
  /** Start-up checks answered but not verified (READINESS, AWAITING_VERIFY): booked missed. */
  startupUnverified: number;
  /** Product checks (first-off, in-process) due and not taken: booked missed. */
  productDue: number;
  /** Owed checks not yet due: removed, never counted. */
  later: number;
}

/** Due by `nowMs`. An unreadable due time counts as due: asking is the safe side.
 *  The close route's own test is `DueAt <= EndedAt`; LineCheck uses this one too,
 *  so "due now" on the screen and "due" in this panel cannot drift apart. */
export function isDueBy(o: { dueAt: string }, nowMs: number): boolean {
  const t = Date.parse(o.dueAt);
  return Number.isNaN(t) || t <= nowMs;
}

/** The three counts closing books missed, plus what it removes. Pure, for tests. */
export function closeCounts(
  owed: readonly CloseObligation[],
  awaitingVerify: readonly CloseObligation[],
  nowMs: number,
): CloseCounts {
  const open = owed.filter((o) => o.status === 'OWED');
  const due = open.filter((o) => isDueBy(o, nowMs));
  return {
    startupUnanswered: due.filter((o) => o.kind === 'READINESS').length,
    // Only READINESS waits on a signature; the close route books only those (Kind = 'READINESS').
    startupUnverified: awaitingVerify.filter((o) => o.status === 'AWAITING_VERIFY' && o.kind === 'READINESS').length,
    productDue: due.filter((o) => o.kind !== 'READINESS').length,
    later: open.length - due.length,
  };
}

/** One read of a status's rows. Null when the envelope carries no list: not read is not none (rule 15). */
async function readObligations(runId: number, status: string | null): Promise<CloseObligation[] | null> {
  const r = (await api.get(
    `/runs/${runId}/obligations${status ? `?status=${status}` : ''}`,
  )) as { obligations?: unknown };
  return Array.isArray(r?.obligations) ? (r.obligations as CloseObligation[]) : null;
}

interface CloseRunProps {
  runId: number;
  /** The product, so the question names what is being closed (UX-39). */
  productName?: string | null;
  /** This account's readings still on the device; null = the queue is unreadable. */
  queued: number | null;
  onClosed: () => void;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function CloseRun({ runId, productName, queued, onClosed }: CloseRunProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={`ml-auto min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
      >
        Close run
      </button>
      {open ? (
        <CloseSheet
          runId={runId}
          productName={productName ?? null}
          queued={queued}
          onClosed={onClosed}
          onCancel={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

interface CloseSheetProps {
  runId: number;
  productName: string | null;
  queued: number | null;
  onClosed: () => void;
  onCancel: () => void;
}

/**
 * The question, as an overlay sheet (UI/UX audit, 31 Oct 2026, UX-41 and UX-46).
 *
 * It used to render inline as `basis-full` in the run's header row, which on the
 * docked iPad pushed the walk-around down to 48px of checks while it was open. A
 * sheet over the page leaves the page where it was. Focus goes to the question on
 * open and back to "Close run" on Cancel or Escape - it used to drop to BODY both
 * ways - and the page behind is `inert` meanwhile, ConfirmPanel's pattern.
 * Mounting is opening: the counts are read fresh each time.
 */
function CloseSheet({ runId, productName, queued, onClosed, onCancel }: CloseSheetProps) {
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** undefined = still reading; null = could not be read (rule 15); else the counts. */
  const [counts, setCounts] = useState<CloseCounts | null | undefined>(undefined);
  const heading = useRef<HTMLHeadingElement>(null);
  const host = useRef<HTMLDivElement | null>(null);
  if (host.current === null && typeof document !== 'undefined') {
    host.current = document.createElement('div');
  }

  const readCounts = useCallback(() => {
    setCounts(undefined);
    Promise.all([readObligations(runId, null), readObligations(runId, 'AWAITING_VERIFY')])
      .then(([owed, awaiting]) =>
        setCounts(owed === null || awaiting === null ? null : closeCounts(owed, awaiting, Date.now())),
      )
      .catch(() => setCounts(null));
  }, [runId]);

  useEffect(readCounts, [readCounts]);

  // Focus in on open, back to the opener on close; the rest of the page inert
  // meanwhile. One effect so the three undo in the right order.
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.appendChild(el);
    const made: Element[] = [];
    for (const child of Array.from(document.body.children)) {
      if (child === el || child.hasAttribute('inert')) continue;
      child.setAttribute('inert', '');
      made.push(child);
    }
    heading.current?.focus();
    return () => {
      for (const c of made) c.removeAttribute('inert');
      el.remove();
      opener?.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !closing) {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, closing]);

  const blocked =
    queued === null
      ? 'This device cannot read its own queue, so it cannot tell whether readings are still ' +
        'waiting to send. Closing now could refuse them. Reload the app, or close the run from ' +
        'another device.'
      : queued > 0
        ? `${plural(queued, 'reading or photo is', 'readings or photos are')} still on this device. Close the run once ` +
          `${queued === 1 ? 'it has' : 'they have'} sent — a reading that arrives after the run is ` +
          'closed is refused.'
        : counts === undefined
          ? null
          : counts === null
            ? 'The checks this run owes could not be read, so what closing would record is not ' +
              'known. Try again.'
            : null;

  const confirm = async () => {
    if (blocked || closing || !counts) return;
    setClosing(true);
    setError(null);
    try {
      await api.post(`/runs/${runId}/close`);
      onClosed();
    } catch (e: unknown) {
      // The server's sentence: "no current CAPTURE authorisation", "already
      // closed". Both are things the person can act on; a generic failure is not.
      setError(opMessage(e));
      setClosing(false);
    }
  };

  if (!host.current) return null;

  const missedLines = counts
    ? [
        counts.startupUnanswered > 0
          ? `${plural(counts.startupUnanswered, 'start-up check', 'start-up checks')} not answered`
          : null,
        counts.startupUnverified > 0
          ? `${plural(counts.startupUnverified, 'start-up check', 'start-up checks')} answered but not verified ` +
            `(${counts.startupUnverified === 1 ? 'it' : 'they'} cannot be verified once the run is closed)`
          : null,
        counts.productDue > 0
          ? `${plural(counts.productDue, 'product check', 'product checks')} due and not taken`
          : null,
      ].filter((l): l is string => l !== null)
    : [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-(--s4) sm:items-center">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="close-run-title"
        className="max-h-full w-full max-w-[640px] overflow-y-auto rounded-(--radius) border-2 border-(--warn-line) bg-(--warn-bg) p-(--s4) text-(--fg)"
      >
        <h2
          id="close-run-title"
          ref={heading}
          tabIndex={-1}
          className={`m-0 text-(length:--fs-lg) font-bold ${opFocus}`}
        >
          Close run {runId}
          {productName ? ` (${productName})` : ''}?
        </h2>
        {blocked ? (
          <div className="mt-(--s2)">
            <p role="alert" className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
              {blocked}
            </p>
            {counts === null && queued !== null && queued === 0 ? (
              <button
                type="button"
                onClick={readCounts}
                className={`mt-(--s3) min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
              >
                Try again
              </button>
            ) : null}
          </div>
        ) : counts === undefined ? (
          <p role="status" className="m-0 mt-(--s2) text-(length:--fs-md) text-(--muted)">
            Reading what closing would record…
          </p>
        ) : counts ? (
          <>
            <p className="m-0 mt-(--s2) text-(length:--fs-md)">
              Closing ends this run for good. Nothing more can be captured against it.
            </p>
            {missedLines.length > 0 ? (
              <>
                <p className="m-0 mt-(--s2) text-(length:--fs-md) font-semibold">
                  These will be recorded as missed:
                </p>
                <ul className="m-0 mt-(--s1) list-disc pl-(--s5) text-(length:--fs-md) font-semibold">
                  {missedLines.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="m-0 mt-(--s2) text-(length:--fs-md) font-semibold">
                Nothing that is due is outstanding, so nothing will be recorded as missed.
              </p>
            )}
            {counts.later > 0 ? (
              <p className="m-0 mt-(--s2) text-(length:--fs-md) text-(--muted)">
                {plural(counts.later, 'check scheduled for later is', 'checks scheduled for later are')} removed —
                the run ends before {counts.later === 1 ? 'it is' : 'they are'} due.
              </p>
            ) : null}
          </>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 mt-(--s2) text-(length:--fs-md) font-semibold text-(--warn)">
            {error}
          </p>
        ) : null}
        <div className="mt-(--s3) flex gap-(--s3)">
          <button
            type="button"
            disabled={blocked !== null || !counts || closing}
            onClick={() => void confirm()}
            className={`min-h-[56px] flex-1 rounded-(--radius) border border-(--border-strong) bg-(--primary) px-(--s4) text-(length:--fs-lg) font-bold text-(--primary-fg) disabled:opacity-50 ${opFocus}`}
          >
            {closing ? 'Closing…' : `Close run ${runId}`}
          </button>
          <button
            type="button"
            disabled={closing}
            onClick={onCancel}
            className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold disabled:opacity-50 ${opFocus}`}
          >
            Cancel
          </button>
        </div>
      </section>
    </div>,
    host.current,
  );
}

export default CloseRun;
